import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Menu as BasicMenu, MenuItem, ListItemIcon } from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAccountLists } from '@/dashboard/AccountLists/useAccountLists/useAccountLists';
import { ListMenuItem } from '@/reports/sections/ListsSection/ListsSections/ListMenuItem';
import { ListForm } from '@/reports/sections/ListsSection/index';
import { AddReportToListV2AddressPayload } from '@/reports/types/serialized';
import { Icons, Text, Divider } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { ReportActionButton } from './components';

interface ReportActionsProps {
  reportData?: AddReportToListV2AddressPayload;
  reportType?: ValidReportTypes;
  reportTitle: string;
  reportPermaLink: string;
  inReportActions?: boolean;
}

interface MenuItem {
  icon?: JSX.Element;
  display: string;
  action: () => void;
}

export function ListAction({
  reportData,
  reportType,
  reportTitle,
  reportPermaLink,
  inReportActions,
}: ReportActionsProps) {
  const { trackEvent } = useAppConfigContext();
  const { redirect } = useRedirect();
  const { data: lists, createList } = useAccountLists();
  const element = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [listFormOpen, setListFormOpen] = useState(false);
  const [foundInList, setFoundInList] = useState(false);
  const open = Boolean(anchorEl);

  const menuActions = [
    {
      display: 'Add to new list',
      action: () => {
        trackEvent(
          'add to list entry point',
          'engagement click',
          'rats component'
        );
        setListFormOpen(true);
      },
      icon: <Icons.Add fontSize="small" color="primary" />,
    },
    {
      display: 'Manage saved reports and lists',
      action: () => {
        redirect('/dashboard/lists');
      },
      icon: <Icons.Settings fontSize="small" color="primary" />,
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackEvent('Lists', 'engagement click', 'rats component');
    setAnchorEl(event.currentTarget);
    setListFormOpen(false);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseItem = (clickedItem: MenuItem) => {
    clickedItem.action();
  };

  useEffect(() => {
    setListFormOpen(false);
  }, [anchorEl]);

  useEffect(() => {
    if (lists) {
      findIfAReportIsInAList();
    }
  }, [lists]);

  const findIfAReportIsInAList = () => {
    let found = false;
    lists
      ?.filter((list) => list.name !== 'saved reports')
      .forEach((list) => {
        list?.list_reports?.forEach((report) => {
          if (report?.report?.permalink === reportPermaLink) {
            found = true;
          }
        });
      });

    setFoundInList(Boolean(found));
  };
  const isOnNewDesign = useFeatureIsOn('RFRSH-2096');

  const iconStyle = isOnNewDesign
    ? undefined
    : { color: foundInList ? 'white' : 'primary' };
  const reportActionButtonStyle = isOnNewDesign
    ? { color: 'white' }
    : { color: foundInList ? 'white' : 'primary' };
  return (
    <>
      <ReportActionButton
        data-testid="list-btn"
        id="list-button-action"
        aria-controls="basic-menu"
        aria-haspopup="true"
        aria-expanded="true"
        ref={element}
        variant="contained"
        color={foundInList ? 'primary' : 'info'}
        onClick={handleClick}
        startIcon={<Icons.List sx={iconStyle} />}
        disableElevation
        sx={reportActionButtonStyle}
      >
        List
      </ReportActionButton>
      <BasicMenu
        id="list-basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
      >
        {listFormOpen ? (
          <ListForm
            closeForm={handleCloseMenu}
            reportType={reportType}
            reportTitle={reportTitle}
            reportData={reportData}
            permalink={reportPermaLink}
            title="Create new list"
            mainActionButtonText="Create list"
            placeholderText="List name"
            action={createList}
            key={reportPermaLink}
          />
        ) : (
          <div key="list-menu">
            {inReportActions &&
              menuActions.map((item) => (
                <MenuItem
                  data-testid="list-actions-menu"
                  key={`menu-action-${item.display}`}
                  onClick={() => handleCloseItem(item)}
                >
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  <Text color="primary">{item.display}</Text>
                </MenuItem>
              ))}
            <Divider sx={{ width: '87%', margin: '12px auto' }} />
            {lists?.map((list) => {
              if (list) {
                return (
                  <ListMenuItem
                    key={list.id}
                    list={list}
                    reportType={reportType}
                    reportPermaLink={reportPermaLink}
                    reportData={reportData}
                  />
                );
              }
            })}
          </div>
        )}
      </BasicMenu>
    </>
  );
}
